import React, { useEffect, useState } from 'react';
import SubmitButton from '../../components/SubmitButton';
import SmallButton from '../../components/SmallButton';
import { RouteComponentProps, useParams, withRouter } from 'react-router';
import {
  Order,
  OrderBoxesResponse,
  OrderResponse,
  BoxChange,
  BoxChangeType,
} from '../../models/APIModels';
import Moment from 'moment';
import Title from '../../components/Title';
import Label from '../../components/Label';
import StatusBlock from '../../components/StatusBlock';
import { axiosKK } from '../../services/networkRequest';
import PaymentBlock from '../../components/PaymentBlock';
import { RouteProps } from 'react-router-dom';
import { toMoney } from '../../utilities/MoneyConversion';
import PaymentStatusBlock from '../../components/PaymentStatusBlock';
import { NoteBlock } from '../../components/NoteBlock';
import Barcode from '../../components/Barcode';
import { downloadBoxLabel } from '../../utilities/PdfDownload';
import { isEmptyValue } from '../../utilities/ValueHelpers';
import useGtmEventTracking from '../../components/useGtmEventTracking';
import { ArrowLeftCircleIcon } from '@heroicons/react/24/solid';

const isPrintableBox = (boxId: number): boolean => {
  return boxId !== 5 && boxId !== 7;
};

interface OrderParams {
  id: string;
}

function OrderComponent({ history }: RouteComponentProps): JSX.Element {
  const { id } = useParams<OrderParams>();
  const [order, setOrder] = useState<Order>();
  const [boxChanges, setBoxChanges] = useState<BoxChange[]>([]);
  const [boxChangesLoading, setBoxChangesLoading] = useState(true);
  const gtmEventTracking = useGtmEventTracking();
  const statuses = order?.statuses || [];
  const payments = order?.payments || [];
  const notes = order?.notes || [];
  const orderBoxes = order?.orderBoxes || [];

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!order) {
      // Try to fetch as regular order first
      axiosKK.get<OrderResponse>(`order/${id}`)
        .then((response) => {
          setOrder(response.data.order);
        })
        .catch(() => {
          // If that fails, try to fetch as removal order
          axiosKK.get<OrderResponse>(`removals/order/${id}`)
            .then((response) => {
              setOrder(response.data.order);
            })
            .catch(() => {
              // Error fetching order
            });
        });
    }
  }, [order, id]);

  useEffect(() => {
    // Fetch box changes for this order
    if (id) {
      setBoxChangesLoading(true);

      // Use the correct endpoint: /order/box-changes (not /box-changes)
      axiosKK.get('/order/box-changes')
        .then((response) => {
          // Handle potential Response wrapper
          let boxChangesData = response.data;

          // Check if response is wrapped in Response object
          if (boxChangesData.data && boxChangesData.data.boxChanges) {
            boxChangesData = boxChangesData.data;
          }

          // Check if boxChanges exist
          if (boxChangesData.boxChanges) {
            // Filter for this specific order
            const orderBoxChanges = boxChangesData.boxChanges.filter(
              (change: BoxChange) => change.orderId === parseInt(id)
            );

            setBoxChanges(orderBoxChanges);
          } else {
            setBoxChanges([]);
          }
          setBoxChangesLoading(false);
        })
        .catch((error) => {
          // If 404, the endpoint doesn't exist yet
          if (error.response?.status === 404) {
            // Box changes endpoint not found. Backend may not be deployed or routing is incorrect.
          }

          setBoxChanges([]);
          setBoxChangesLoading(false);
        });
    }
  }, [id]);

  useEffect(() => {
    if (order) {
      gtmEventTracking({
        event: 'order_page_viewed',
        orderId: order.id,
        orderType: order.orderType === 'removal'
          ? 'removal'
          : order.suitcaseOrder
            ? 'suitcase'
            : order.storageRequired
              ? 'storage'
              : 'shipping',
        hasDeliveryDate: !isEmptyValue(order.deliveryDate),
        hasDeliveryAddress: !isEmptyValue(order.deliveryAddress),
        numberOfBoxes: order.orderBoxes?.length || 0
      });
    }
  }, [order, gtmEventTracking]);

  return (
    <>
      <section className="nav-status-bar">
        <div className="status-bar-content">
          <ArrowLeftCircleIcon width={20} height={20} />
          <SmallButton
            onClick={() => {
              gtmEventTracking({
                event: 'order_back_clicked',
                orderId: id
              });
              history.push('/dashboard');
            }}
          >
            Back
          </SmallButton>
        </div>
      </section>
      <Title>Your Order Overview</Title>
      <div className="print-doc customer-print-doc">
        <div className="top-border">
          {order?.orderType === 'removal'
            ? 'Removal Order'
            : order?.suitcaseOrder
              ? 'Suitcase Order'
              : order?.storageRequired
                ? 'Storage Order'
                : 'Shipping Order'}
        </div>
        <div className="second-border">
          <p className="date">
            Order Created At: {order?.createdAt ? Moment(order.createdAt).format('dddd, MMM Do YYYY') : 'N/A'}
          </p>
        </div>
        <div className="basic-block">
          <div className="id-city-requirement">
            <div className="id-and-city">
              <p className="id">{order?.id}</p>
              <p className="city">
                {order?.orderType === 'removal'
                  ? 'Removal'
                  : order?.suitcaseOrder
                    ? 'Shipping'
                    : order?.storageRequired
                      ? 'Storage'
                      : 'Shipping'}
              </p>
            </div>
          </div>
          <div className="addresses-block">
            <div className="each">
              <Label>{order?.orderType === 'removal' ? 'Move Date & Collection Address:' : 'Collection Date & Address:'}</Label>
              <p>{order?.collectionAddress}</p>
              <p>{order?.orderType === 'removal' 
                ? order?.moveDate && Moment(order.moveDate).format('dddd, MMM Do YYYY')
                : order?.collectionDate && Moment(order.collectionDate).format('dddd, MMM Do YYYY')}
              </p>
            </div>
            <div className="each">
              <Label>Delivery {order?.orderType === 'removal' ? '' : 'Date & '}Address:</Label>
              {isEmptyValue(order?.deliveryAddress) && isEmptyValue(order?.deliveryDate) ? (
                <p>Not Set Yet</p>
              ) : (
                <>
                  {!isEmptyValue(order?.deliveryAddress) && <p>{order?.deliveryAddress}</p>}
                  {!isEmptyValue(order?.deliveryDate) && (
                    <p>{Moment(order?.deliveryDate).format('dddd, MMM Do YYYY')}</p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {order?.orderType === 'removal' ? (
          <div className="items-block">
            <div className="eachBox">
              <div className="border-top" />
              <div className="content">
                <div className="basic">
                  <h3>Removal Service</h3>
                  <p><strong>Tier:</strong> {order?.tierName}</p>
                </div>
                <div className="cost">
                  <Label htmlFor={`removal-details-${order?.id}`}>Property Details:</Label>
                  <div className="costDetails">
                    <Label htmlFor={`removal-floor-${order?.id}`}>
                      Property Floor:
                    </Label>
                    <p id={`removal-floor-${order?.id}`}>
                      {order?.propertyFloor === '0' ? 'Ground Floor' : `Floor ${order?.propertyFloor}`}
                    </p>
                    {order?.propertyHasLift !== null && (
                      <>
                        <Label htmlFor={`removal-lift-${order?.id}`}>
                          Lift Available:
                        </Label>
                        <p id={`removal-lift-${order?.id}`}>
                          {order?.propertyHasLift ? 'Yes' : 'No'}
                        </p>
                      </>
                    )}
                    <Label htmlFor={`removal-furniture-${order?.id}`}>
                      Furniture:
                    </Label>
                    <p id={`removal-furniture-${order?.id}`}>
                      {order?.propertyHasFurniture ? 'Yes' : 'No'}
                    </p>
                    <Label htmlFor={`removal-parking-${order?.id}`}>
                      Parking Available:
                    </Label>
                    <p id={`removal-parking-${order?.id}`}>
                      {order?.propertyHasParking ? 'Yes' : 'No'}
                    </p>
                  </div>
                </div>
                {(order?.packingMaterialsId || order?.storageId) && (
                  <div className="cost">
                    <Label htmlFor={`removal-extras-${order?.id}`}>Extras:</Label>
                    <div className="costDetails">
                      {order?.packingMaterialsId && (
                        <>
                          <Label htmlFor={`removal-packing-${order?.id}`}>
                            Packing Materials:
                          </Label>
                          <p id={`removal-packing-${order?.id}`}>
                            Included - {toMoney(order?.packingMaterialsPrice || 0)}
                          </p>
                        </>
                      )}
                      {order?.storageId && (
                        <>
                          <Label htmlFor={`removal-storage-${order?.id}`}>
                            Storage:
                          </Label>
                          <p id={`removal-storage-${order?.id}`}>
                            {order?.storageSize} - {toMoney(order?.storageMonthlyPrice || 0)}/month
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                )}
                <div className="cost">
                  <Label htmlFor={`removal-cost-breakdown-${order?.id}`}>Cost Breakdown:</Label>
                  <div className="costDetails">
                    <Label htmlFor={`removal-base-cost-${order?.id}`}>
                      Base Move Cost ({order?.tierName}):
                    </Label>
                    <p id={`removal-base-cost-${order?.id}`}>
                      {toMoney(order?.baseCost || 0)}
                    </p>
                    {(order?.extrasCost || 0) > 0 && (
                      <>
                        <Label htmlFor={`removal-extras-cost-${order?.id}`}>
                          Extras Cost:
                        </Label>
                        <p id={`removal-extras-cost-${order?.id}`}>
                          {toMoney(order?.extrasCost || 0)}
                        </p>
                      </>
                    )}
                    {order?.discountCode && (order?.discountApplied || 0) > 0 && (
                      <>
                        <Label htmlFor={`removal-discount-${order?.id}`}>
                          Discount ({order?.discountCode} - {order?.discountPercentage}%):
                        </Label>
                        <p id={`removal-discount-${order?.id}`} style={{ color: '#28a745' }}>
                          -{toMoney(order?.discountApplied || 0)}
                        </p>
                      </>
                    )}
                    <Label htmlFor={`removal-total-cost-${order?.id}`}>
                      <strong>Total Initial Cost:</strong>
                    </Label>
                    <p id={`removal-total-cost-${order?.id}`}>
                      <strong>{toMoney(order?.totalInitialCost || 0)}</strong>
                    </p>
                    {order?.storageId && (order?.monthlyStorageCost || 0) > 0 && (
                      <>
                        <Label htmlFor={`removal-monthly-cost-${order?.id}`}>
                          <strong>Monthly Storage Cost:</strong>
                        </Label>
                        <p id={`removal-monthly-cost-${order?.id}`}>
                          <strong>{toMoney(order?.monthlyStorageCost || 0)}/month</strong>
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="items-block">
            {orderBoxes.map((orderBox, index) => {
              return (
                <OrderBoxesDisplay
                  key={index}
                  id={orderBox.id}
                  orderId={order?.id}
                  suitcaseOrder={order?.suitcaseOrder}
                  storageRequired={order?.storageRequired}
                  barcode={orderBox?.barcode}
                  orderBox={orderBox}
                />
              );
            })}
            {boxChangesLoading && (
              <div style={{ padding: '1rem', color: '#666', fontStyle: 'italic' }}>
                Loading box changes...
              </div>
            )}
            {!boxChangesLoading && boxChanges.length > 0 && (
              <BoxChangesBlock boxChanges={boxChanges} />
            )}
          </div>
        )}

        <NoteBlock notes={notes} />
        <StatusBlock statuses={statuses} />
        <PaymentStatusBlock payments={payments} />
        <PaymentBlock
          initialCost={order?.orderType === 'removal' ? order?.totalInitialCost : order?.initialCost}
          monthlyCost={order?.orderType === 'removal' ? order?.monthlyStorageCost : order?.monthlyCost}
          storageRequired={order?.orderType === 'removal' ? !!order?.storageId : order?.storageRequired}
        />
      </div>
      {order?.orderType !== 'removal' && (
        <div className="btn-row">
          <SubmitButton
            red={true}
            onClick={() => {
              gtmEventTracking({
                event: 'order_edit_clicked',
                orderId: id
              });
              history.push(`/order/${id}/edit`);
            }}
          >
            Edit
          </SubmitButton>
        </div>
      )}
    </>
  );
}

interface BoxChangesBlockProps {
  boxChanges: BoxChange[];
}

function BoxChangesBlock({ boxChanges }: BoxChangesBlockProps): JSX.Element {
  return (
    <>
      {boxChanges.map((change) => (
        <div
          key={change.id}
          className={`eachBox box-change-box ${
            change.changeType === BoxChangeType.ADDED
              ? 'box-change-added'
              : 'box-change-removed'
          }`}
        >
          <div className="border-top" />
          <div className="content">
            <div className="box-change-badge">
              {change.changeType === BoxChangeType.ADDED ? (
                <>
                  <svg
                    className="box-change-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  <span className="box-change-label">ADDED</span>
                </>
              ) : (
                <>
                  <svg
                    className="box-change-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M20 12H4"
                    />
                  </svg>
                  <span className="box-change-label">REMOVED</span>
                </>
              )}
            </div>
            <div className="basic">
              <h3>{change.boxName}</h3>
              {change.barcode && (
                <p className="box-change-barcode">Barcode: <Barcode>{change.barcode}</Barcode></p>
              )}
              {change.reason && (
                <p className="box-change-reason">{change.reason}</p>
              )}
            </div>
            <div className="cost">
              <Label htmlFor={`boxChange-details-${change.id}`}>Change Details:</Label>
              <div className="costDetails">
                <Label htmlFor={`boxChange-date-${change.id}`}>Date:</Label>
                <p id={`boxChange-date-${change.id}`}>
                  {Moment(change.createdAt).format('dddd, MMM Do YYYY')}
                </p>
                {change.changedBy && (
                  <>
                    <Label htmlFor={`boxChange-by-${change.id}`}>Changed By:</Label>
                    <p id={`boxChange-by-${change.id}`}>{change.changedBy}</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

interface OrderBoxesDisplayProps extends RouteProps {
  orderBox: OrderBoxesResponse;
  id: number;
  orderId?: number;
  suitcaseOrder?: boolean;
  storageRequired?: boolean;
  barcode?: string;
}

export function OrderBoxesDisplay({
  orderBox,
  id,
  orderId,
  suitcaseOrder,
  storageRequired,
  barcode,
}: OrderBoxesDisplayProps): JSX.Element {
  const [isDownloading, setIsDownloading] = useState(false);
  const gtmEventTracking = useGtmEventTracking();
  const orderBoxDetail = orderBox?.box;
  const dimensions = orderBox?.dimensions;

  const handleDownloadLabel = async () => {
    if (!orderId) return;
    setIsDownloading(true);
    gtmEventTracking({
      event: 'box_label_downloaded',
      orderId: orderId,
      boxId: orderBox.id,
      boxName: orderBoxDetail?.name
    });
    try {
      await downloadBoxLabel(orderId, orderBox.id);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download label. Please try again or contact support.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <div className="eachBox">
        <div className="border-top" />
        <div className="content">
          {suitcaseOrder ? (
            <h3>Suitcase</h3>
          ) : (
            <img
              className="boxIcon"
              src={orderBoxDetail?.imageUrl}
              alt="Product"
            />
          )}
          <div className="basic">
            {orderBox?.id && (
              <span className="box-id-badge">#{orderBox.id}</span>
            )}
            <h3 id={`orderBoxDetail-name-${id}`}>{orderBoxDetail?.name}</h3>
          </div>
          <div className="cost">
            <Label htmlFor={`orderBoxDetail-cost-${id}`}>Costs:</Label>
            <div className="costDetails">
              {suitcaseOrder ? (
                <>
                  <Label htmlFor={`orderBoxDetail-suitcaseDimensions-${id}`}>
                    Dimensions:
                  </Label>
                  <div className="suitcase-mobile-view-table">
                    <ul>
                      <li>
                        <span className="title">Dimensions (cm)</span>
                        <h4 className="blue">
                          {dimensions?.depth} x {dimensions?.height} x{' '}
                          {dimensions?.width}
                        </h4>
                      </li>
                      <li>
                        <span className="title">Weight (kg)</span>
                        <h4 className="blue">{dimensions?.weight}</h4>
                      </li>
                    </ul>
                  </div>
                </>
              ) : (
                <>
                  <Label htmlFor={`orderBoxDetail-purchaseCost-${id}`}>
                    Purchase Cost:
                  </Label>
                  <p id={`orderBoxDetail-purchaseCost-${id}`}>
                    {orderBoxDetail?.purchaseCost &&
                      toMoney(orderBoxDetail.purchaseCost)}
                  </p>
                </>
              )}
              {!suitcaseOrder && (
                <>
                  <Label htmlFor={`orderBoxDetail-storageCost-${id}`}>
                    Storage Cost:
                  </Label>
                  <p id={`orderBoxDetail-storageCost-${id}`}>
                    {orderBoxDetail?.storageCost &&
                      toMoney(orderBoxDetail.storageCost)}
                  </p>
                </>
              )}
            </div>
            {storageRequired && !suitcaseOrder && orderId && isPrintableBox(orderBox.boxId) && (
              <div className="download-label-action" style={{ marginTop: '12px' }}>
                <button
                  onClick={handleDownloadLabel}
                  disabled={isDownloading}
                  className="download-label-btn"
                  style={{
                    opacity: isDownloading ? 0.6 : 1,
                    cursor: isDownloading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isDownloading ? 'Downloading...' : 'Download Label (PDF)'}
                </button>
              </div>
            )}
          </div>
          {barcode && (
            <div className="barcode">
              <Barcode>{barcode}</Barcode>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default withRouter(OrderComponent);
